import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Search, Filter, Maximize2, Network, Users, Info, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;

// Custom ODP marker icons by status - square shape with antenna/tower symbol (different from customer pin)
const createIcon = (color) => new L.DivIcon({
  className: 'odp-marker',
  html: `<div style="
    position: relative; width: 32px; height: 36px;
    display: flex; align-items: flex-end; justify-content: center;
  ">
    <div style="
      position: absolute; top: 0; left: 50%; transform: translateX(-50%);
      width: 0; height: 0;
      border-left: 4px solid transparent; border-right: 4px solid transparent;
      border-bottom: 6px solid ${color};
    "></div>
    <div style="
      position: absolute; top: 4px; left: 50%; transform: translateX(-50%);
      width: 24px; height: 24px;
      background: linear-gradient(135deg, ${color}, ${color}dd);
      border: 2.5px solid white;
      border-radius: 6px;
      box-shadow: 0 3px 8px rgba(0,0,0,0.35);
      display: flex; align-items: center; justify-content: center;
      color: white; font-weight: bold; font-size: 11px;
      font-family: -apple-system, BlinkMacSystemFont, sans-serif;
    ">ODP</div>
    <div style="
      position: absolute; bottom: 0; left: 50%; transform: translateX(-50%);
      width: 6px; height: 6px;
      background: ${color};
      border: 2px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    "></div>
  </div>`,
  iconSize: [32, 36],
  iconAnchor: [16, 36],
  popupAnchor: [0, -36],
});

const markerIcons = {
  active: createIcon('#10b981'),
  full: createIcon('#ef4444'),
  maintenance: createIcon('#f59e0b'),
  inactive: createIcon('#94a3b8'),
};

const statusLabels = {
  active: 'Aktif', full: 'Penuh', maintenance: 'Maintenance', inactive: 'Nonaktif',
};

// Component to fit map to markers
const FitBounds = ({ markers }) => {
  const map = useMap();
  useEffect(() => {
    if (markers.length > 0) {
      const bounds = L.latLngBounds(markers.map(m => [m.latitude, m.longitude]));
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
    }
  }, [markers, map]);
  return null;
};

const ODPMap = () => {
  const { user } = useAuth();
  const userBranchId = user?.branch_id ? String(user.branch_id) : null;
  const isBranchScoped = ['kepala_cabang', 'teknisi', 'customer service'].includes(String(user?.role || '').toLowerCase()) && userBranchId;
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ branch_id: isBranchScoped ? userBranchId : '', owner_id: '', search: '' });
  const [filterOptions, setFilterOptions] = useState({ branches: [], owners: [] });
  const [selectedODP, setSelectedODP] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState({ active: 0, full: 0, maintenance: 0, inactive: 0 });
  const mapRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };

  // Default center: Depok, Indonesia
  const defaultCenter = [-6.402, 106.794];

  const fetchMapData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.branch_id) params.append('branch_id', filters.branch_id);
      if (filters.owner_id) params.append('owner_id', filters.owner_id);
      if (filters.search) params.append('search', filters.search);

      const res = await fetch(`${API_URL}/api/odp-pop/map-data?${params}`, { headers });
      const data = await res.json();
      if (data.success) {
        const items = data.data || [];
        setMarkers(items);
        // Calculate stats
        const s = { active: 0, full: 0, maintenance: 0, inactive: 0 };
        items.forEach(i => { if (s[i.status] !== undefined) s[i.status]++; });
        setStats(s);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchFilterOptions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/odp-pop/filters/options`, { headers });
      const data = await res.json();
      if (data.success) setFilterOptions({ branches: data.branches, owners: data.owners });
    } catch (e) { /* silent */ }
  };

  useEffect(() => { fetchMapData(); }, [filters]);
  useEffect(() => { fetchFilterOptions(); }, []);

  return (
    <div data-testid="odp-map" className="space-y-4 p-3 sm:p-5 lg:p-6">
      {/* Header with Stats */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2 dark:text-slate-200">
          <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" /> Peta Lokasi ODP/POP
        </h2>
        <div className="flex items-center gap-2">
          {[
            { key: 'active', color: '#10b981' },
            { key: 'full', color: '#ef4444' },
            { key: 'maintenance', color: '#f59e0b' },
            { key: 'inactive', color: '#94a3b8' },
          ].map(s => (
            <div key={s.key} className="flex items-center gap-1 text-xs">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }}></div>
              <span className="text-slate-600 dark:text-slate-300">{statusLabels[s.key]} ({stats[s.key]})</span>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border p-3 dark:bg-slate-800">
        <div className="flex flex-wrap items-center gap-3">
          <select data-testid="map-filter-branch"
            className={"h-9 border rounded-md px-3 text-sm " + (isBranchScoped ? "bg-slate-100 cursor-not-allowed" : "")}
            disabled={isBranchScoped}
            title={isBranchScoped ? 'Anda hanya dapat melihat data cabang Anda sendiri' : undefined}
            value={filters.branch_id}
            onChange={e => { if (isBranchScoped) return; setFilters(f => ({ ...f, branch_id: e.target.value })); }}>
            {!isBranchScoped && <option value="">Semua Cabang</option>}
            {filterOptions.branches.filter(b => !isBranchScoped || String(b.branch_id) === userBranchId).map((b, i) => (
              <option key={i} value={b.branch_id}>{b.branch_name}</option>
            ))}
          </select>
          <select data-testid="map-filter-owner" className="h-9 border rounded-md px-3 text-sm"
            value={filters.owner_id} onChange={e => setFilters(f => ({ ...f, owner_id: e.target.value }))}
            title={isBranchScoped ? 'Hanya menampilkan owner di cabang Anda' : undefined}>
            <option value="">{isBranchScoped ? 'Semua Owner Cabang Ini' : 'Semua Owner'}</option>
            {filterOptions.owners.map((o, i) => (
              <option key={i} value={o.owner_id}>{o.owner_name}</option>
            ))}
          </select>
          <div className="flex-1 min-w-[180px]">
            <Input data-testid="map-search" placeholder="Cari nama / area..."
              value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              className="h-9" />
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400">
            {markers.length} ODP/POP ditampilkan
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="bg-white rounded-lg border overflow-hidden dark:bg-slate-800" style={{ height: 'calc(100vh - 280px)', minHeight: '400px' }}>
        {loading && markers.length === 0 ? (
          <div className="flex items-center justify-center h-full text-slate-400 dark:text-slate-500">
            <div className="text-center">
              <MapPin className="w-8 h-8 mx-auto mb-2 animate-pulse" />
              <p>Memuat peta...</p>
            </div>
          </div>
        ) : (
          <MapContainer
            center={markers.length > 0 ? [markers[0].latitude, markers[0].longitude] : defaultCenter}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            ref={mapRef}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {markers.length > 0 && <FitBounds markers={markers} />}
            {markers.map(item => (
              <Marker
                key={item.id}
                position={[parseFloat(item.latitude), parseFloat(item.longitude)]}
                icon={markerIcons[item.status] || markerIcons.active}
                eventHandlers={{ click: () => setSelectedODP(item) }}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <h3 style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>{item.name}</h3>
                    <p style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{item.address || 'Tidak ada alamat'}</p>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '4px',
                        background: item.status === 'active' ? '#d1fae5' : item.status === 'full' ? '#fee2e2' : item.status === 'maintenance' ? '#fef3c7' : '#f1f5f9',
                        color: item.status === 'active' ? '#059669' : item.status === 'full' ? '#dc2626' : item.status === 'maintenance' ? '#d97706' : '#64748b'
                      }}>
                        {statusLabels[item.status]}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px' }}>
                      <div><strong>Port:</strong> {item.used_ports || 0}/{item.capacity || 0}</div>
                      {item.branch_name && <div><strong>Cabang:</strong> {item.branch_name}</div>}
                      {item.owner_name && <div><strong>Owner:</strong> {item.owner_name}</div>}
                      <div><strong>Customer:</strong> {item.linked_customers || 0}</div>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </div>
    </div>
  );
};

export default ODPMap;
